/**
 * The PATCH an edited schedule sends: what the operator changed, and
 * nothing else.
 *
 * Every field the api-server is sent is a field it acts on (0.6.54):
 *
 *  - `rrule` / `dtstart_ms` / `timezone` differing from the stored row is
 *    a TIMING change, and a schedule with history is re-anchored for it —
 *    its dtstart moves to the first new run, a COUNT is recounted, skips
 *    and moves the new rule no longer produces are deleted. Sending them
 *    unchanged-but-respelled re-times a schedule nobody re-timed.
 *  - `task` replaces the whole stored task and clears the task bodies of
 *    future per-run overrides, so the series edit reaches them.
 *  - `until_ms: null` CLEARS the end; leaving it out keeps it.
 *  - Unknown fields are a 422 (`extra='forbid'`), so a `TaskSchedule`
 *    spread into the body fails outright.
 *
 * So the body is built field by field from a comparison with the stored
 * row, never from the edited values wholesale.
 */
import type { TaskRequest, TaskSchedule, UpdateTaskScheduleRequest } from '../../api/v1';
import { timingChanged } from './rrule';

/** The editable fields of a schedule, as the editor holds them after an
 *  edit. `timezone` is the SCHEDULE's own and should come back as it
 *  was read: the create form writes the site's zone, and an editor that
 *  did the same would move a schedule stored in another zone onto it,
 *  changing every run's identity. */
export interface ScheduleEdit {
  readonly name: string;
  readonly task: TaskRequest;
  readonly rrule: string;
  readonly dtstart_ms: number;
  readonly timezone: string;
  readonly until_ms: number | null;
}

/**
 * The body for `useUpdateSchedule`, or null when nothing changed.
 *
 * A timing change sends all three timing fields together with
 * `applyFromMs` (omitted when null: the server applies it from now);
 * any other change never carries `apply_from_ms`, which the server
 * would ignore anyway. The name is compared and sent trimmed — the
 * caller refuses an empty one before getting here, as the server would.
 */
export function buildSchedulePatch(
  before: TaskSchedule,
  after: ScheduleEdit,
  applyFromMs?: number | null,
): UpdateTaskScheduleRequest | null {
  const patch: { -readonly [K in keyof UpdateTaskScheduleRequest]: UpdateTaskScheduleRequest[K] } =
    {};
  const name = after.name.trim();
  if (name !== before.name) patch.name = name;
  if (!sameJson(before.task, after.task)) patch.task = after.task;
  if ((before.until_ms ?? null) !== after.until_ms) patch.until_ms = after.until_ms;
  if (timingChanged(before, after)) {
    patch.rrule = after.rrule;
    patch.dtstart_ms = after.dtstart_ms;
    patch.timezone = after.timezone;
    if (applyFromMs !== null && applyFromMs !== undefined) patch.apply_from_ms = applyFromMs;
  }
  return Object.keys(patch).length > 0 ? patch : null;
}

/** Structural equality over JSON values — key order ignored, as it is
 *  on the wire. A task rebuilt by `taskRequestFromForm` from an
 *  untouched form IS the stored object, so this is the backstop for one
 *  assembled any other way. */
function sameJson(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((v, i) => sameJson(v, b[i]));
  }
  const ra = a as Record<string, unknown>;
  const rb = b as Record<string, unknown>;
  // `undefined` is absent on the wire.
  const ka = Object.keys(ra).filter((k) => ra[k] !== undefined);
  const kb = Object.keys(rb).filter((k) => rb[k] !== undefined);
  return ka.length === kb.length && ka.every((k) => sameJson(ra[k], rb[k]));
}
